import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Nav, Wordmark } from '../components';
import { ShoppingBag, X, Plus, Minus, Trash2, ArrowRight, Star, Truck, Shield, RefreshCw } from 'lucide-react';

// Sample merchandise data
const products = [
  {
    id: 1,
    name: 'VentyVIP Event Tee',
    description: 'Premium cotton tee with subtle branding. Perfect for any event.',
    price: 32,
    image: '/shop/event-tee.jpg',
    category: 'Apparel',
    sizes: ['S', 'M', 'L', 'XL', 'XXL'],
    colors: ['Black', 'White', 'Navy'],
    rating: 4.8,
    reviews: 124
  },
  {
    id: 2,
    name: 'VIP Access Hoodie',
    description: 'Ultra-soft hoodie with embroidered logo. Stay warm in style.',
    price: 68,
    image: '/shop/vip-hoodie.jpg',
    category: 'Apparel',
    sizes: ['S', 'M', 'L', 'XL', 'XXL'],
    colors: ['Black', 'Heather Gray'],
    rating: 4.9,
    reviews: 89
  },
  {
    id: 3,
    name: 'Event Planner Notebook',
    description: 'Hardcover notebook for planning your perfect event experience.',
    price: 24,
    image: '/shop/notebook.jpg',
    category: 'Accessories',
    rating: 4.7,
    reviews: 56
  },
  {
    id: 4,
    name: 'VentyVIP Tote Bag',
    description: 'Durable canvas tote with reinforced handles. Fits all your event essentials.',
    price: 28,
    image: '/shop/tote.jpg',
    category: 'Accessories',
    colors: ['Natural', 'Black'],
    rating: 4.6,
    reviews: 42
  },
  {
    id: 5,
    name: 'Enamel Pin Set',
    description: 'Collectible enamel pins featuring iconic event designs. Set of 3.',
    price: 18,
    image: '/shop/pins.jpg',
    category: 'Accessories',
    rating: 4.9,
    reviews: 203
  },
  {
    id: 6,
    name: 'Phone Case',
    description: 'Protective case with slim profile and VentyVIP branding.',
    price: 22,
    image: '/shop/phone-case.jpg',
    category: 'Tech',
    rating: 4.5,
    reviews: 31
  }
];

const categories = ['All', 'Apparel', 'Accessories', 'Tech'];

export const Shop = () => {
  const { t } = useTranslation();
  const [activeCategory, setActiveCategory] = useState('All');
  const [cart, setCart] = useState([]);
  const [isCartOpen, setIsCartOpen] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [selectedSize, setSelectedSize] = useState('');
  const [selectedColor, setSelectedColor] = useState('');

  const filteredProducts = activeCategory === 'All' 
    ? products 
    : products.filter(p => p.category === activeCategory);

  const addToCart = (product) => {
    const existingItem = cart.find(item => 
      item.id === product.id && 
      item.size === (selectedSize || product.sizes?.[0]) &&
      item.color === (selectedColor || product.colors?.[0])
    );

    if (existingItem) {
      setCart(cart.map(item => 
        item.id === existingItem.id && 
        item.size === existingItem.size &&
        item.color === existingItem.color
          ? { ...item, quantity: item.quantity + 1 }
          : item
      ));
    } else {
      setCart([...cart, {
        ...product,
        size: selectedSize || product.sizes?.[0],
        color: selectedColor || product.colors?.[0],
        quantity: 1,
        cartId: Date.now()
      }]);
    }
    setIsCartOpen(true);
    setSelectedProduct(null);
  };

  const removeFromCart = (cartId) => {
    setCart(cart.filter(item => item.cartId !== cartId));
  };

  const updateQuantity = (cartId, delta) => {
    setCart(cart.map(item => {
      if (item.cartId === cartId) {
        const newQuantity = Math.max(1, item.quantity + delta);
        return { ...item, quantity: newQuantity };
      }
      return item;
    }));
  };

  const cartTotal = cart.reduce((sum, item) => sum + (item.price * item.quantity), 0);
  const cartCount = cart.reduce((sum, item) => sum + item.quantity, 0);

  return (
    <div style={{ minHeight: '100vh', background: 'var(--bg)' }}>
      <Nav />
      
      {/* Cart Drawer */}
      {isCartOpen && (
        <>
          <div 
            style={{
              position: 'fixed',
              inset: 0,
              background: 'rgba(0,0,0,0.5)',
              zIndex: 100,
              backdropFilter: 'blur(4px)'
            }}
            onClick={() => setIsCartOpen(false)}
          />
          <div style={{
            position: 'fixed',
            top: 0,
            right: 0,
            width: '100%',
            maxWidth: 420,
            height: '100vh',
            background: 'var(--bg)',
            zIndex: 101,
            display: 'flex',
            flexDirection: 'column',
            boxShadow: '-4px 0 24px rgba(0,0,0,0.15)'
          }}>
            <div style={{
              padding: '20px 24px',
              borderBottom: '1px solid var(--line)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between'
            }}>
              <h2 style={{ fontSize: 20, fontWeight: 600 }}>Your Cart ({cartCount})</h2>
              <button 
                onClick={() => setIsCartOpen(false)}
                style={{ padding: 8, borderRadius: 999, border: 'none', background: 'transparent', cursor: 'pointer' }}
              >
                <X size={24} />
              </button>
            </div>
            
            <div style={{ flex: 1, overflow: 'auto', padding: 24 }}>
              {cart.length === 0 ? (
                <div style={{ textAlign: 'center', padding: '40px 20px', color: 'var(--ink-3)' }}>
                  <ShoppingBag size={48} style={{ marginBottom: 16, opacity: 0.5 }} />
                  <p>Your cart is empty</p>
                  <button 
                    onClick={() => setIsCartOpen(false)}
                    style={{
                      marginTop: 16,
                      padding: '10px 20px',
                      background: 'var(--primary)',
                      color: '#fff',
                      border: 'none',
                      borderRadius: 999,
                      cursor: 'pointer'
                    }}
                  >
                    Continue Shopping
                  </button>
                </div>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
                  {cart.map((item) => (
                    <div 
                      key={item.cartId}
                      style={{
                        display: 'flex',
                        gap: 16,
                        padding: 16,
                        background: 'var(--bg-2)',
                        borderRadius: 12,
                        border: '1px solid var(--line)'
                      }}
                    >
                      <div style={{
                        width: 80,
                        height: 80,
                        background: 'var(--bg-3)',
                        borderRadius: 8,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontSize: 32
                      }}>
                        🛍️
                      </div>
                      <div style={{ flex: 1 }}>
                        <h4 style={{ fontSize: 14, fontWeight: 600, marginBottom: 4 }}>{item.name}</h4>
                        <p style={{ fontSize: 12, color: 'var(--ink-3)', marginBottom: 8 }}>
                          {item.size && `Size: ${item.size}`} 
                          {item.size && item.color && ' · '}
                          {item.color && `Color: ${item.color}`}
                        </p>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                            <button 
                              onClick={() => updateQuantity(item.cartId, -1)}
                              style={{ 
                                width: 28, 
                                height: 28, 
                                borderRadius: 6,
                                border: '1px solid var(--line)',
                                background: 'var(--bg)',
                                cursor: 'pointer',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center'
                              }}
                            >
                              <Minus size={14} />
                            </button>
                            <span style={{ fontSize: 14, fontWeight: 500, minWidth: 20, textAlign: 'center' }}>
                              {item.quantity}
                            </span>
                            <button 
                              onClick={() => updateQuantity(item.cartId, 1)}
                              style={{ 
                                width: 28, 
                                height: 28, 
                                borderRadius: 6,
                                border: '1px solid var(--line)',
                                background: 'var(--bg)',
                                cursor: 'pointer',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center'
                              }}
                            >
                              <Plus size={14} />
                            </button>
                          </div>
                          <span style={{ fontSize: 14, fontWeight: 600, marginLeft: 'auto' }}>
                            ${(item.price * item.quantity).toFixed(2)}
                          </span>
                        </div>
                      </div>
                      <button 
                        onClick={() => removeFromCart(item.cartId)}
                        style={{ 
                          padding: 4, 
                          border: 'none', 
                          background: 'transparent', 
                          cursor: 'pointer',
                          color: 'var(--ink-3)'
                        }}
                      >
                        <Trash2 size={18} />
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
            
            {cart.length > 0 && (
              <div style={{ 
                padding: 24, 
                borderTop: '1px solid var(--line)',
                background: 'var(--bg-2)'
              }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8 }}>
                  <span style={{ color: 'var(--ink-2)' }}>Subtotal</span>
                  <span style={{ fontWeight: 500 }}>${cartTotal.toFixed(2)}</span>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 16 }}>
                  <span style={{ color: 'var(--ink-2)' }}>Shipping</span>
                  <span style={{ color: 'var(--success)' }}>Free</span>
                </div>
                <div style={{ 
                  display: 'flex', 
                  justifyContent: 'space-between', 
                  marginBottom: 20,
                  paddingTop: 12,
                  borderTop: '1px solid var(--line)'
                }}>
                  <span style={{ fontSize: 18, fontWeight: 600 }}>Total</span>
                  <span style={{ fontSize: 18, fontWeight: 600 }}>${cartTotal.toFixed(2)}</span>
                </div>
                <button 
                  style={{
                    width: '100%',
                    padding: '14px 24px',
                    background: 'var(--primary)',
                    color: '#fff',
                    border: 'none',
                    borderRadius: 999,
                    fontSize: 16,
                    fontWeight: 600,
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: 8
                  }}
                >
                  Checkout <ArrowRight size={18} />
                </button>
              </div>
            )}
          </div>
        </>
      )}

      {/* Product Modal */}
      {selectedProduct && (
        <>
          <div 
            style={{
              position: 'fixed',
              inset: 0,
              background: 'rgba(0,0,0,0.6)',
              zIndex: 100,
              backdropFilter: 'blur(4px)'
            }}
            onClick={() => setSelectedProduct(null)}
          />
          <div style={{
            position: 'fixed',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            width: '90%',
            maxWidth: 600,
            maxHeight: '90vh',
            overflow: 'auto',
            background: 'var(--bg)',
            zIndex: 101,
            borderRadius: 16,
            boxShadow: '0 24px 48px rgba(0,0,0,0.2)'
          }}>
            <div style={{ position: 'relative' }}>
              <button 
                onClick={() => setSelectedProduct(null)}
                style={{ 
                  position: 'absolute',
                  top: 16,
                  right: 16,
                  zIndex: 10,
                  padding: 8,
                  borderRadius: 999,
                  border: 'none',
                  background: 'rgba(255,255,255,0.9)',
                  cursor: 'pointer'
                }}
              >
                <X size={24} />
              </button>
              <div style={{
                height: 300,
                background: 'var(--bg-2)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: 80
              }}>
                🛍️
              </div>
            </div>
            <div style={{ padding: 32 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12 }}>
                <span style={{ 
                  padding: '4px 12px',
                  background: 'var(--primary-tint)',
                  color: 'var(--primary)',
                  borderRadius: 999,
                  fontSize: 12,
                  fontWeight: 500
                }}>
                  {selectedProduct.category}
                </span>
                <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                  <Star size={14} fill="currentColor" style={{ color: '#FBAB18' }} />
                  <span style={{ fontSize: 14, fontWeight: 500 }}>{selectedProduct.rating}</span>
                  <span style={{ fontSize: 14, color: 'var(--ink-3)' }}>({selectedProduct.reviews})</span>
                </div>
              </div>
              <h2 style={{ fontSize: 28, fontWeight: 600, marginBottom: 8 }}>{selectedProduct.name}</h2>
              <p style={{ fontSize: 24, fontWeight: 700, color: 'var(--primary)', marginBottom: 16 }}>
                ${selectedProduct.price.toFixed(2)}
              </p>
              <p style={{ fontSize: 16, color: 'var(--ink-2)', lineHeight: 1.6, marginBottom: 24 }}>
                {selectedProduct.description}
              </p>
              
              {selectedProduct.sizes && (
                <div style={{ marginBottom: 20 }}>
                  <label style={{ display: 'block', fontSize: 14, fontWeight: 500, marginBottom: 8 }}>
                    Size
                  </label>
                  <div style={{ display: 'flex', gap: 8 }}>
                    {selectedProduct.sizes.map(size => (
                      <button
                        key={size}
                        onClick={() => setSelectedSize(size)}
                        style={{
                          width: 48,
                          height: 48,
                          borderRadius: 8,
                          border: `2px solid ${selectedSize === size ? 'var(--primary)' : 'var(--line)'}`,
                          background: selectedSize === size ? 'var(--primary-tint)' : 'var(--bg)',
                          color: selectedSize === size ? 'var(--primary)' : 'var(--ink)',
                          fontWeight: 600,
                          cursor: 'pointer'
                        }}
                      >
                        {size}
                      </button>
                    ))}
                  </div>
                </div>
              )}
              
              {selectedProduct.colors && (
                <div style={{ marginBottom: 24 }}>
                  <label style={{ display: 'block', fontSize: 14, fontWeight: 500, marginBottom: 8 }}>
                    Color
                  </label>
                  <div style={{ display: 'flex', gap: 8 }}>
                    {selectedProduct.colors.map(color => (
                      <button
                        key={color}
                        onClick={() => setSelectedColor(color)}
                        style={{
                          padding: '10px 20px',
                          borderRadius: 8,
                          border: `2px solid ${selectedColor === color ? 'var(--primary)' : 'var(--line)'}`,
                          background: selectedColor === color ? 'var(--primary-tint)' : 'var(--bg)',
                          color: selectedColor === color ? 'var(--primary)' : 'var(--ink)',
                          fontWeight: 500,
                          cursor: 'pointer'
                        }}
                      >
                        {color}
                      </button>
                    ))}
                  </div>
                </div>
              )}
              
              <button 
                onClick={() => addToCart(selectedProduct)}
                style={{
                  width: '100%',
                  padding: '16px 24px',
                  background: 'var(--primary)',
                  color: '#fff',
                  border: 'none',
                  borderRadius: 999,
                  fontSize: 16,
                  fontWeight: 600,
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: 8
                }}
              >
                <ShoppingBag size={20} /> Add to Cart — ${selectedProduct.price.toFixed(2)}
              </button>
            </div>
          </div>
        </>
      )}

      {/* Hero Section */}
      <section style={{ padding: '100px 24px 60px', background: 'var(--bg-2)' }}>
        <div style={{ maxWidth: 1200, margin: '0 auto', textAlign: 'center' }}>
          <div style={{ fontFamily: 'var(--font-mono)', fontSize: 12, letterSpacing: '.14em', textTransform: 'uppercase', color: 'var(--ink-3)', marginBottom: 16 }}>
            Official Merchandise
          </div>
          <h1 style={{ fontSize: 'clamp(40px, 6vw, 72px)', lineHeight: 1.05, letterSpacing: '-0.02em', marginBottom: 20 }}>
            Shop VentyVIP
          </h1>
          <p style={{ fontSize: 18, color: 'var(--ink-2)', maxWidth: 540, margin: '0 auto', lineHeight: 1.6 }}>
            Premium merchandise for event enthusiasts. Wear your passion.
          </p>
        </div>
      </section>

      {/* Features */}
      <section style={{ padding: '32px 24px', background: 'var(--bg)', borderBottom: '1px solid var(--line)' }}>
        <div style={{ maxWidth: 1200, margin: '0 auto' }}>
          <div style={{ display: 'flex', justifyContent: 'center', gap: 48, flexWrap: 'wrap' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
              <Truck size={24} style={{ color: 'var(--primary)' }} />
              <span style={{ fontSize: 14 }}>Free Shipping on $50+</span>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
              <Shield size={24} style={{ color: 'var(--primary)' }} />
              <span style={{ fontSize: 14 }}>30-Day Returns</span>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
              <RefreshCw size={24} style={{ color: 'var(--primary)' }} />
              <span style={{ fontSize: 14 }}>Secure Checkout</span>
            </div>
          </div>
        </div>
      </section>

      {/* Cart Button */}
      <div style={{ 
        position: 'fixed', 
        top: 100, 
        right: 24, 
        zIndex: 50 
      }}>
        <button 
          onClick={() => setIsCartOpen(true)}
          style={{
            width: 56,
            height: 56,
            borderRadius: 999,
            background: 'var(--primary)',
            color: '#fff',
            border: 'none',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
            position: 'relative'
          }}
        >
          <ShoppingBag size={24} />
          {cartCount > 0 && (
            <span style={{
              position: 'absolute',
              top: -4,
              right: -4,
              width: 22,
              height: 22,
              borderRadius: 999,
              background: '#E8321A',
              color: '#fff',
              fontSize: 12,
              fontWeight: 600,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}>
              {cartCount}
            </span>
          )}
        </button>
      </div>

      {/* Product Grid */}
      <section style={{ padding: '60px 24px' }}>
        <div style={{ maxWidth: 1200, margin: '0 auto' }}>
          {/* Category Filter */}
          <div style={{ display: 'flex', gap: 12, marginBottom: 40, flexWrap: 'wrap', justifyContent: 'center' }}>
            {categories.map(cat => (
              <button
                key={cat}
                onClick={() => setActiveCategory(cat)}
                style={{
                  padding: '10px 20px',
                  borderRadius: 999,
                  border: '1px solid var(--line)',
                  background: activeCategory === cat ? 'var(--primary)' : 'var(--bg)',
                  color: activeCategory === cat ? '#fff' : 'var(--ink)',
                  fontSize: 14,
                  fontWeight: 500,
                  cursor: 'pointer'
                }}
              >
                {cat}
              </button>
            ))}
          </div>

          {/* Products */}
          <div style={{ 
            display: 'grid', 
            gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', 
            gap: 32 
          }}>
            {filteredProducts.map(product => (
              <div 
                key={product.id}
                style={{
                  background: 'var(--bg)',
                  borderRadius: 16,
                  border: '1px solid var(--line)',
                  overflow: 'hidden',
                  cursor: 'pointer',
                  transition: 'transform 0.2s, box-shadow 0.2s'
                }}
                onClick={() => {
                  setSelectedProduct(product);
                  setSelectedSize(product.sizes?.[0] || '');
                  setSelectedColor(product.colors?.[0] || '');
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.transform = 'translateY(-4px)';
                  e.currentTarget.style.boxShadow = 'var(--shadow-lg)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = 'translateY(0)';
                  e.currentTarget.style.boxShadow = 'none';
                }}
              >
                <div style={{
                  height: 240,
                  background: 'var(--bg-2)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: 64
                }}>
                  🛍️
                </div>
                <div style={{ padding: 24 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
                    <span style={{ 
                      padding: '2px 8px',
                      background: 'var(--primary-tint)',
                      color: 'var(--primary)',
                      borderRadius: 999,
                      fontSize: 11,
                      fontWeight: 500
                    }}>
                      {product.category}
                    </span>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                      <Star size={12} fill="currentColor" style={{ color: '#FBAB18' }} />
                      <span style={{ fontSize: 12, fontWeight: 500 }}>{product.rating}</span>
                    </div>
                  </div>
                  <h3 style={{ fontSize: 18, fontWeight: 600, marginBottom: 8 }}>{product.name}</h3>
                  <p style={{ fontSize: 14, color: 'var(--ink-2)', marginBottom: 16, lineHeight: 1.5 }}>
                    {product.description.slice(0, 80)}...
                  </p>
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <span style={{ fontSize: 20, fontWeight: 700, color: 'var(--primary)' }}>
                      ${product.price.toFixed(2)}
                    </span>
                    <button 
                      onClick={(e) => {
                        e.stopPropagation();
                        addToCart(product);
                      }}
                      style={{
                        padding: '10px 16px',
                        background: 'var(--primary)',
                        color: '#fff',
                        border: 'none',
                        borderRadius: 999,
                        fontSize: 14,
                        fontWeight: 500,
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        gap: 6
                      }}
                    >
                      <ShoppingBag size={16} /> Add
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer style={{ padding: '60px 24px', background: 'var(--bg-2)', borderTop: '1px solid var(--line)' }}>
        <div style={{ maxWidth: 1200, margin: '0 auto' }}>
          <div style={{ textAlign: 'center' }}>
            <Wordmark style={{ height: 28, marginBottom: 16 }} />
            <p style={{ fontSize: 14, color: 'var(--ink-3)', marginBottom: 24 }}>
              © 2026 VentyVIP. All rights reserved.
            </p>
            <div style={{ display: 'flex', justifyContent: 'center', gap: 24 }}>
              <Link to="/" style={{ color: 'var(--ink-2)', textDecoration: 'none', fontSize: 14 }}>Home</Link>
              <Link to="/blog" style={{ color: 'var(--ink-2)', textDecoration: 'none', fontSize: 14 }}>Blog</Link>
              <Link to="/about" style={{ color: 'var(--ink-2)', textDecoration: 'none', fontSize: 14 }}>About</Link>
              <Link to="/privacy" style={{ color: 'var(--ink-2)', textDecoration: 'none', fontSize: 14 }}>Privacy</Link>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};
